import React, { useState, useEffect, useRef } from "react";
import CustomButton from "../components/ui/Button";
import SolutionDropdown from "../components/solutiondropdown/SolutionDropdown";
import ServicesDropdown from "../components/servicesdropdown/ServicesDropdown";
import VendorsDropdown from "../components/vendorsdropdown/VendorsDropdown";
import { Link, useLocation } from "react-router-dom";
import { fetchServices } from "../components/api/SolutionApi";
import { useSolutionDropdown } from "../components/hooks/SolutionDropdownContext";
import DropdownErrorPage from "../components/not_found/DropdownNotFound";
import { useTranslation } from "react-i18next";
import { useDropdownHandlers } from "../components/hooks/useDropdownHandlers";

const Navbar = () => {
  const { t, i18n } = useTranslation("navbar"); // Hook to use translations
  const isRussian = i18n.language === "ru";
  const { 
    isDropdownOpen, 
    toggleDropdown, 
    closeDropdown, 
    isMenuOpen, 
    toggleMenu, 
    closeMenu,
    isMobile,
    startCloseTimer,
    cancelCloseTimer
  } = useSolutionDropdown();
  const [ActivePage, setActivePage] = useState("Home");
  const [services, setServices] = useState([]);
  const [error, setError] = useState(null);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isVendorsOpen, setIsVendorsOpen] = useState(false);

  const location = useLocation();
  const dropdownRef = useRef(null);
  const navbarRef = useRef(null);
  const solutionButtonRef = useRef(null);
  const servicesButtonRef = useRef(null);
  const vendorsButtonRef = useRef(null);

  const {
    handleSolutionClick,
    handleSolutionMouseEnter,
    handleMenuMouseLeave,
    handleMenuMouseEnter,
    handleServicesClick,
    handleServicesMouseEnter,
    handleServicesMouseLeave,
    handleServicesClose,
    handleVendorsClick,
    handleVendorsMouseEnter,
    handleVendorsMouseLeave,
    handleVendorsClose
  } = useDropdownHandlers(
    isMobile,
    isDropdownOpen,
    isServicesOpen,
    isVendorsOpen,
    toggleDropdown,
    closeDropdown,
    setIsServicesOpen,
    setIsVendorsOpen,
    cancelCloseTimer,
    startCloseTimer
  );

  const handlePageChange = (page) => {
    setActivePage(page);
    closeMenu();
  };

  const handleClickOutside = (event) => {
    if (
      navbarRef.current &&
      !navbarRef.current.contains(event.target) &&
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target)
    ) {
      closeMenu();
    }
  };

  // Fetch services data when the Navbar component is mounted
  useEffect(() => {
    const getServices = async () => {
      try {
        const data = await fetchServices();
        setServices(data.data);
      } catch (err) {
        setError("Failed to fetch services");
      }
    };

    getServices();
  }, []);

  // Scroll to top when the location changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  });

  const renderDropdownContent = () => {
    if (error) {
      return <DropdownErrorPage title="failed_to_fetch_service" description="service_not_found_description" />;
    }
    if (services.length === 0) {
      return <DropdownErrorPage title="service_not_found_title" description="service_not_found_description" />;
    }
    return (
      <SolutionDropdown
        services={services}
        onClose={() => {
          closeDropdown();
          if (isMobile) {
            closeMenu();
          }
        }}
        triggerRef={solutionButtonRef}
        onMouseLeave={handleMenuMouseLeave}
        onMouseEnter={handleMenuMouseEnter}
      />
    );
  };

  return (
    <nav ref={navbarRef} className="sticky bg-black fixed w-full z-20 top-0 start-0 py-7 px-4 top-[37px]">
      <div className="container-class flex items-center mx-auto">
        {/* Mobile Menu Button */}
        <button
          data-collapse-toggle="navbar-sticky"
          type="button"
          className="inline-flex items-center p-2 w-10 h-10 justify-center text-gray-500 rounded-lg xl:hidden"
          aria-controls="navbar-sticky"
          aria-expanded={isMenuOpen ? "true" : "false"}
          onClick={toggleMenu}
        >
          <span className="sr-only">Open main menu</span>
          <svg
            className="w-5 h-5"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 17 14"
          >
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M1 1h15M1 7h15M1 13h15"
            />
          </svg>
        </button>

        {/* Logo */}
        <a href="/" className="flex items-center space-x-3">
          <img
            src="/assets/logo.png"
            className="hidden md:block w-[140px] lg:w-[250px]"
            alt="Logo"
          />
          <img
            src="/assets/half-logo.svg"
            className="block md:hidden w-[60px]"
            alt="Mobile Logo"
          />
        </a>

        <div className="flex md:order-2" />


        {/* Navigation Links */}
        <div
          className={`items-center justify-between w-full xl:flex xl:w-auto xl:order-1 absolute xl:relative top-[100px] xl:top-0 left-0 ml-0 xl:ml-[160px] ${isMenuOpen ? "block bg-black w-full" : "hidden"}`}
          id="navbar-sticky"
        >
          <ul className="flex flex-col xl:flex-row bg-[rgb(32,32,32)] xl:bg-transparent mb-2 xl:mb-0">
            <li className={`relative  py-[10px] ${isRussian ? 'px-[15px]' : 'px-[18px]'} `}>
              <Link
                to="/"
                onClick={() => handlePageChange("Home")}
                className={`block py-2 px-3 font-normal rounded md:p-0 ${location.pathname === "/"
                  ? "text-[#05897E]"
                  : "text-[#F0F0F0B2] hover:text-[#05897E]"
                  }`}
              >
                {t("home")}
              </Link>
            </li>
            <li
              className={`relative py-[10px] ${isRussian ? 'px-[15px]' : 'px-[18px]'} `}
              ref={dropdownRef}
              onMouseEnter={() => {
                handleMenuMouseEnter();
                handleSolutionMouseEnter();
              }}
              onMouseLeave={handleMenuMouseLeave}
            >
              <Link
                ref={solutionButtonRef}
                to="#"
                onClick={handleSolutionClick}
                onMouseEnter={() => {
                  handleMenuMouseEnter();
                  handleSolutionMouseEnter();
                }}
                onMouseLeave={handleMenuMouseLeave}
                className="block py-2 px-3 font-normal rounded md:p-0 text-[#F0F0F0B2] hover:text-[#05897E]"
              >
                {t("solution")}
              </Link>
              {isDropdownOpen && renderDropdownContent()}
            </li>
            <li 
              className={`relative py-[10px] ${isRussian ? 'px-[15px]' : 'px-[18px]'}`}
              ref={servicesButtonRef}
              onMouseEnter={handleServicesMouseEnter}
              onMouseLeave={handleServicesMouseLeave}
            >
              <Link
                to="/services"
                onClick={handleServicesClick}
                onMouseEnter={handleServicesMouseEnter}
                onMouseLeave={handleServicesMouseLeave}
                className={`block py-2 px-3 font-normal rounded md:p-0 ${
                  location.pathname === "/services"
                    ? "text-[#05897E]"
                    : "text-[#F0F0F0B2] hover:text-[#05897E]"
                }`}
              >
                {t("services")}
              </Link>
              {isServicesOpen && (
                <ServicesDropdown
                  onClose={handleServicesClose}
                  triggerRef={servicesButtonRef}
                  onMouseEnter={handleServicesMouseEnter}
                  onMouseLeave={handleServicesMouseLeave}
                  className="services-dropdown"
                />
              )}
            </li>
            <li 
              className={`relative py-[10px] ${isRussian ? 'px-[15px]' : 'px-[18px]'}`}
              ref={vendorsButtonRef}
              onMouseEnter={handleVendorsMouseEnter}
              onMouseLeave={handleVendorsMouseLeave}
            >
              <Link
                to="/distributor_certificates"
                onClick={handleVendorsClick}
                onMouseEnter={handleVendorsMouseEnter}
                onMouseLeave={handleVendorsMouseLeave}
                className={`block py-2 px-3 font-normal rounded md:p-0 ${
                  location.pathname === "/distributor_certificates"
                    ? "text-[#05897E]"
                    : "text-[#F0F0F0B2] hover:text-[#05897E]"
                }`}
              >
                {t("vendors")}
              </Link>
              {isVendorsOpen && (
                <VendorsDropdown
                  onClose={handleVendorsClose}
                  triggerRef={vendorsButtonRef}
                  onMouseEnter={handleVendorsMouseEnter}
                  onMouseLeave={handleVendorsMouseLeave}
                />
              )}
            </li>
            
            <li className={`relative px-[18px] py-[10px] ${isRussian ? 'px-[15px]' : ''} `}>
              <Link
                to="/products_from_vendors"
                onClick={() => handlePageChange("Library")}
                className={`block py-2 px-3 font-normal rounded md:p-0 ${location.pathname === "/products_from_vendors"
                  ? "text-[#05897E]"
                  : "text-[#F0F0F0B2] hover:text-[#05897E]"
                  }`}
              >
                {t("projects")}
              </Link>
            </li>
            <li className={`relative px-[18px] py-[10px] ${isRussian ? 'px-[15px]' : ''} `}>
              <Link
                to="/library"
                onClick={() => handlePageChange("Library")}
                className={`block py-2 px-3 font-normal rounded md:p-0 ${location.pathname === "/library"
                  ? "text-[#05897E]"
                  : "text-[#F0F0F0B2] hover:text-[#05897E]"
                  }`}
              >
                {t("library")}
              </Link>
            </li>
            <li className={`relative px-[18px] py-[10px] ${isRussian ? 'px-[15px]' : ''} `}>
              <Link
                to="/contact_us"
                onClick={() => handlePageChange("Contact us")}
                className={`block py-2 px-3 font-normal rounded md:p-0 ${location.pathname === "/contact_us"
                  ? "text-[#05897E]"
                  : "text-[#F0F0F0B2] hover:text-[#05897E]"
                  }`}
              >
                {t("contact:contact_information")}
              </Link>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
